import {
    DayPicker,
    useDayPicker,
    type ClassNames,
    type CustomComponents,
    type Matcher,
} from '@daypicker/react';
import { cva } from 'class-variance-authority';
import { addMonths, format, isValid, startOfMonth, type Locale } from 'date-fns';
import { ChevronDownIcon } from 'lucide-react';
import {
    createContext,
    useContext,
    useLayoutEffect,
    useRef,
    useState,
    type ComponentPropsWithRef,
    type ReactElement,
} from 'react';
import { cn } from '../util/classes.ts';
import { useBreakpoint } from '../util/use-breakpoint.tsx';
import { useControllableState } from '../util/use-controllable-state.tsx';

const calendarVariants = cva(
    [
        'flex flex-col text-ui-base text-content',
        '[--dp-cell-pad:2px]',
        '[--dp-cell:calc(var(--dp-day)+var(--dp-cell-pad)*2)]',
        '[--dp-caption:2.25rem]',
        '[--dp-weekday:1.75rem]',
        '[--dp-body:calc(var(--dp-weekday)+6*var(--dp-cell))]',
        '[--dp-picker-w:calc(7*var(--dp-cell))]',
    ],
    {
        variants: {
            touch: {
                true: 'w-full [--dp-day:2.75rem]',
                false: 'w-fit [--dp-day:2rem]',
            },
        },
        defaultVariants: { touch: false },
    },
);

const navButton = cn(
    'flex size-interactive shrink-0 cursor-pointer items-center justify-center rounded-tight',
    'text-content-mid transition duration-state ease-state',
    'hover:bg-content/(--opacity-wash)',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
    'aria-disabled:pointer-events-none aria-disabled:opacity-disabled',
);

/**
 * Tailwind against our tokens, handed to `DayPicker` — no stylesheet to keep in
 * sync. Touch widths ride on `in-data-touch:`, hooked to the shell's attribute.
 * Range fills sit on the cell so they run unbroken; the button inside is bare.
 */
const calendarClassNames: Partial<ClassNames> = {
    months: 'flex gap-relaxed in-data-touch:w-full',

    month: 'grid grid-cols-[1fr_auto] items-center in-data-touch:w-full',
    month_caption: 'col-start-1 row-start-1 flex h-(--dp-caption) items-center',
    nav: 'col-start-2 row-start-1 flex items-center gap-tight',

    button_previous: navButton,
    button_next: navButton,
    chevron: 'size-4 shrink-0 fill-current',

    month_grid: 'col-span-2 row-start-2 w-max border-collapse in-data-touch:w-full',
    weekday: cn(
        'h-(--dp-weekday) w-(--dp-cell) text-center text-ui-sm font-medium text-content-weak',
        'in-data-touch:w-auto',
    ),
    day: 'size-(--dp-cell) p-0 text-center align-middle in-data-touch:w-auto',
    day_button: cn(
        'mx-auto flex size-(--dp-day) cursor-pointer items-center justify-center rounded-tight',
        // Cells share the row evenly on touch, and the target fills its cell.
        'in-data-touch:h-(--dp-day) in-data-touch:w-full',
        'tabular-nums transition duration-state ease-state',
        'hover:bg-content/(--opacity-wash)',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        'disabled:pointer-events-none',
    ),

    // DayPicker marks selection through these class names alone — it sets no
    // `aria-selected` — so today's own styling has to stand down against the
    // marker `selected` carries, or its colour fights the selected fill.
    today: cn(
        '[&:not(.day-selected)>button]:font-semibold',
        '[&:not(.day-selected)>button]:text-content-weak',
    ),
    selected: 'day-selected [&>button]:bg-brand [&>button]:text-brand-contrast [&>button]:hover:bg-brand',
    outside: '[&>button]:text-content-weakest',
    disabled: '[&>button]:pointer-events-none [&>button]:text-content-disabled',
    hidden: 'invisible',

    // The ends fill their cell and round only outwards, so the solid block reads
    // as one shape with the band between it rather than a chip floating in it.
    range_start: cn(
        'rounded-l-tight bg-brand/(--opacity-wash)',
        '[&>button]:size-full [&>button]:rounded-l-tight [&>button]:rounded-r-none',
        '[&>button]:bg-brand [&>button]:text-brand-contrast',
    ),
    range_middle: 'bg-brand/(--opacity-wash) [&>button]:bg-transparent [&>button]:text-content',
    range_end: cn(
        'rounded-r-tight bg-brand/(--opacity-wash)',
        '[&>button]:size-full [&>button]:rounded-r-tight [&>button]:rounded-l-none',
        '[&>button]:bg-brand [&>button]:text-brand-contrast',
    ),
};

/** The caption walks years → months, so any month is two taps from the days. */
type PickerView = 'months' | 'years';

/** Which panel has a picker open, if any — the rest show their days. */
type OpenPicker = { panel: number; view: PickerView };

type ViewContextValue = {
    open: OpenPicker | undefined;
    setOpen: (open: OpenPicker | undefined) => void;
    startMonth: Date;
    endMonth: Date;
    locale?: Locale;
};

const ViewContext = createContext<ViewContextValue | undefined>(undefined);

function useViewContext(): ViewContextValue {
    const context = useContext(ViewContext);
    if (!context) {
        throw new Error('Calendar internals must render inside Calendar.');
    }
    return context;
}

function yearsBetween(from: Date, to: Date): number[] {
    const first = from.getFullYear();
    return Array.from({ length: to.getFullYear() - first + 1 }, (_, offset) => first + offset);
}

/** Three columns of options, the one in play scrolled into the middle. */
function PickerGrid({ className, children, ...props }: ComponentPropsWithRef<'div'>): ReactElement {
    const gridRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const grid = gridRef.current;
        const current = grid?.querySelector<HTMLElement>('[aria-current]');
        if (!grid || !current) {
            return;
        }
        // Scroll the grid itself rather than calling scrollIntoView, which would
        // also scroll the sheet the calendar sits in.
        grid.scrollTop = Math.max(0, current.offsetTop - (grid.clientHeight - current.offsetHeight) / 2);
    }, []);

    return (
        <div
            ref={gridRef}
            className={cn(
                'grid h-(--dp-body) grid-cols-3 content-start gap-tight pt-relaxed',
                'overflow-y-auto overscroll-contain',
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
}

/** The caption: `MMMM yyyy` and a chevron, or a bare year in a picker view. */
function CaptionToggle({
    label,
    expanded,
    onClick,
}: {
    label: string;
    expanded: boolean;
    onClick: () => void;
}): ReactElement {
    return (
        // The visible month is the accessible name; `aria-expanded` says the rest.
        <button
            type="button"
            onClick={onClick}
            aria-expanded={expanded}
            className={cn(
                // `w-fit` so a column flex parent (the picker views) can't stretch it.
                'flex h-(--dp-caption) w-fit cursor-pointer items-center gap-tight rounded-tight px-cozy',
                'text-body-emphasis whitespace-nowrap text-content',
                'transition duration-state ease-state hover:bg-content/(--opacity-wash)',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                expanded && 'bg-content/(--opacity-wash)',
            )}
        >
            <span>{label}</span>
            <ChevronDownIcon
                aria-hidden
                className={cn('size-4 shrink-0 text-content-weak', expanded && 'rotate-180')}
            />
        </button>
    );
}

type PickerOption = {
    /** Unique within its list, so it doubles as the React key. */
    label: string;
    selected: boolean;
    isToday: boolean;
    disabled?: boolean;
    onSelect: () => void;
};

/**
 * Replaces the day grid while a picker view is open. Both views are the same
 * panel — a caption that steps back and a grid of options — so only the data
 * below differs.
 */
function MonthPanel({
    calendarMonth,
    displayIndex,
    className,
    children,
    ...props
}: Parameters<CustomComponents['Month']>[0]): ReturnType<CustomComponents['Month']> {
    const { open, setOpen, startMonth, endMonth, locale } = useViewContext();
    const { goToMonth } = useDayPicker();
    const view = open?.panel === displayIndex ? open.view : undefined;

    if (!view) {
        return (
            <div className={className} {...props}>
                {children}
            </div>
        );
    }

    const panelMonth = calendarMonth.date;
    const year = panelMonth.getFullYear();
    const monthIndex = panelMonth.getMonth();
    const today = new Date();

    /** `month` drives the first panel, so a later panel's pick shifts back to it. */
    function show(month: Date): void {
        goToMonth(addMonths(month, -displayIndex));
    }

    const step =
        view === 'years'
            ? {
                  caption: format(panelMonth, 'MMMM yyyy', { locale }),
                  back: undefined,
                  // Years scroll, so they start at the top; twelve months fit.
                  align: undefined,
                  options: yearsBetween(startMonth, endMonth).map<PickerOption>((candidate) => ({
                      label: String(candidate),
                      selected: candidate === year,
                      isToday: candidate === today.getFullYear(),
                      onSelect: () => {
                          show(new Date(candidate, monthIndex, 1));
                          setOpen({ panel: displayIndex, view: 'months' });
                      },
                  })),
              }
            : {
                  caption: String(year),
                  back: { panel: displayIndex, view: 'years' as const },
                  align: 'content-center',
                  options: Array.from({ length: 12 }, (_, index): PickerOption => {
                      const month = new Date(year, index, 1);
                      return {
                          label: format(month, 'LLL', { locale }),
                          selected: index === monthIndex,
                          isToday: index === today.getMonth() && year === today.getFullYear(),
                          disabled: month < startMonth || month > endMonth,
                          onSelect: () => {
                              show(month);
                              setOpen(undefined);
                          },
                      };
                  }),
              };

    return (
        // Replaces `classNames.month`, whose grid puts a caption beside a table.
        <div className="flex w-(--dp-picker-w) flex-col in-data-touch:w-full" {...props}>
            <CaptionToggle
                label={step.caption}
                expanded
                onClick={() => {
                    setOpen(step.back);
                }}
            />
            <PickerGrid className={step.align}>
                {step.options.map((option) => (
                    <button
                        key={option.label}
                        type="button"
                        aria-current={option.selected || undefined}
                        disabled={option.disabled}
                        className={cn(
                            'flex h-interactive cursor-pointer items-center justify-center rounded-tight',
                            'text-ui-base tabular-nums text-content transition duration-state ease-state',
                            'hover:bg-content/(--opacity-wash)',
                            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                            'disabled:pointer-events-none disabled:text-content-disabled',
                            'aria-current:bg-brand aria-current:text-brand-contrast',
                            // Today reads grey and bold here too.
                            option.isToday && !option.selected && 'font-semibold text-content-weak',
                        )}
                        onClick={option.onSelect}
                    >
                        {option.label}
                    </button>
                ))}
            </PickerGrid>
        </div>
    );
}

/** Opens the year grid; DayPicker's own nav stays for stepping one month. */
function MonthCaption({
    calendarMonth,
    displayIndex,
    className,
    ...props
}: Parameters<CustomComponents['MonthCaption']>[0]): ReturnType<CustomComponents['MonthCaption']> {
    const { setOpen, locale } = useViewContext();

    return (
        <div className={className} {...props}>
            <CaptionToggle
                label={format(calendarMonth.date, 'MMMM yyyy', { locale })}
                expanded={false}
                onClick={() => {
                    setOpen({ panel: displayIndex, view: 'years' });
                }}
            />
        </div>
    );
}

const calendarComponents: Partial<CustomComponents> = { Month: MonthPanel, MonthCaption: MonthCaption };

/** DayPicker's range shape, re-exported so callers need not import it. */
export type DateRange = { from: Date | undefined; to?: Date | undefined };

type CalendarSelection =
    | { mode: 'single'; selected: Date | undefined; onSelect: (date: Date | undefined) => void }
    | { mode: 'range'; selected: DateRange | undefined; onSelect: (range: DateRange | undefined) => void };

export type CalendarProps = Omit<ComponentPropsWithRef<'div'>, 'defaultValue' | 'onSelect'> & {
    numberOfMonths?: number;
    month?: Date;
    defaultMonth?: Date;
    onMonthChange?: (month: Date) => void;
    /** Earliest selectable month. Defaults to ten years back. */
    startMonth?: Date;
    /** Latest selectable month. Defaults to ten years forward. */
    endMonth?: Date;
    disabled?: Matcher | Matcher[];
    locale?: Locale;
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
};

type CalendarInternalProps = CalendarProps & CalendarSelection;

/**
 * The day grid. DayPicker does the work — grid, selection, keyboard nav, locale
 * — wrapped in our tokens and a touch layout. The caption is a button that
 * swaps the days for a year grid, then a month grid: two taps to any month,
 * and one target instead of two selects.
 */
export function Calendar({
    mode,
    selected,
    onSelect,
    numberOfMonths = 1,
    month: controlledMonth,
    defaultMonth,
    onMonthChange,
    startMonth,
    endMonth,
    disabled,
    locale,
    weekStartsOn = 1,
    className,
    ...props
}: CalendarInternalProps): ReactElement {
    const touch = !useBreakpoint('sm');
    const [openPicker, setOpenPicker] = useState<OpenPicker | undefined>(undefined);
    const [displayMonth, setDisplayMonth] = useControllableState<Date>({
        value: controlledMonth,
        defaultValue: startOfMonth(defaultMonth ?? anchorOf(selected) ?? new Date()),
        onChange: onMonthChange,
    });

    const [thisYear] = useState(() => new Date().getFullYear());

    const resolvedStart = startMonth ?? new Date(thisYear - 10, 0, 1);
    const resolvedEnd = endMonth ?? new Date(thisYear + 10, 11, 31);

    const commonProps = {
        classNames: calendarClassNames,
        captionLayout: 'label' as const,
        components: calendarComponents,
        navLayout: 'after' as const,
        locale,
        weekStartsOn,
        // Two months never fit a phone.
        numberOfMonths: touch ? 1 : numberOfMonths,
        month: displayMonth,
        onMonthChange: setDisplayMonth,

        startMonth: resolvedStart,
        endMonth: resolvedEnd,
        disabled,
        showOutsideDays: numberOfMonths === 1,
        fixedWeeks: true,
    };

    return (
        <ViewContext
            value={{
                open: openPicker,
                setOpen: setOpenPicker,
                startMonth: resolvedStart,
                endMonth: resolvedEnd,
                locale,
            }}
        >
            <div
                data-touch={touch || undefined}
                className={cn(calendarVariants({ touch }), className)}
                {...props}
            >
                {mode === 'single' ? (
                    <DayPicker {...commonProps} mode="single" selected={selected} onSelect={onSelect} />
                ) : (
                    <DayPicker
                        {...commonProps}
                        mode="range"
                        resetOnSelect
                        selected={selected}
                        onSelect={onSelect}
                    />
                )}
            </div>
        </ViewContext>
    );
}

function anchorOf(selection: Date | DateRange | undefined): Date | undefined {
    if (!selection) {
        return undefined;
    }
    if (selection instanceof Date) {
        return isValid(selection) ? selection : undefined;
    }
    return selection.from ?? selection.to;
}
